import { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
} from '@mui/material';
import {
  Send,
  MessageSquare,
  Users,
  School,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { communicationApi, CommunicationResponse, studentsApi, classroomsApi, Student, Classroom } from '../../services/schoolApi';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Communication = () => {
  const { language } = useLanguage();
  const [tabValue, setTabValue] = useState(0);
  const [message, setMessage] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedClassroom, setSelectedClassroom] = useState('');
  const [students, setStudents] = useState<Student[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [result, setResult] = useState<CommunicationResponse | null>(null);

  const currentText = language === 'fr' ? {
    title: 'Communication',
    subtitle: 'Envoyer des messages SMS aux parents',
    toStudent: 'Message à un élève',
    toClassroom: 'Message à une classe',
    toSchool: 'Message à toute l\'école',
    selectStudent: 'Sélectionner un élève',
    selectClassroom: 'Sélectionner une classe',
    message: 'Message',
    messagePlaceholder: 'Saisissez votre message ici...',
    send: 'Envoyer',
    sending: 'Envoi en cours...',
    messageSent: 'Message envoyé avec succès',
    recipient: 'Destinataire',
    studentName: 'Élève',
    parentName: 'Parent',
    parentPhone: 'Téléphone',
    classroom: 'Classe',
    summary: 'Résumé',
    totalStudents: 'Total élèves',
    studentsWithPhone: 'Avec téléphone',
    messagesSent: 'Messages envoyés',
    messagesFailed: 'Messages échoués',
    results: 'Résultats détaillés',
    matricule: 'Matricule',
    status: 'Statut',
    sent: 'Envoyé',
    failed: 'Échoué',
    byClass: 'Par classe',
    messageLength: 'Longueur du message',
    characters: 'caractères',
    smsCount: 'SMS',
    warning: 'Attention',
    noPhone: 'Certains élèves n\'ont pas de numéro de téléphone',
    confirmSchool: 'Vous allez envoyer ce message à TOUS les parents de l\'école. Êtes-vous sûr ?',
  } : {
    title: 'Communication',
    subtitle: 'Send SMS messages to parents',
    toStudent: 'Message to a student',
    toClassroom: 'Message to a class',
    toSchool: 'Message to entire school',
    selectStudent: 'Select a student',
    selectClassroom: 'Select a classroom',
    message: 'Message',
    messagePlaceholder: 'Enter your message here...',
    send: 'Send',
    sending: 'Sending...',
    messageSent: 'Message sent successfully',
    recipient: 'Recipient',
    studentName: 'Student',
    parentName: 'Parent',
    parentPhone: 'Phone',
    classroom: 'Classroom',
    summary: 'Summary',
    totalStudents: 'Total students',
    studentsWithPhone: 'With phone',
    messagesSent: 'Messages sent',
    messagesFailed: 'Messages failed',
    results: 'Detailed results',
    matricule: 'ID',
    status: 'Status',
    sent: 'Sent',
    failed: 'Failed',
    byClass: 'By class',
    messageLength: 'Message length',
    characters: 'characters',
    smsCount: 'SMS',
    warning: 'Warning',
    noPhone: 'Some students don\'t have a phone number',
    confirmSchool: 'You are about to send this message to ALL parents in the school. Are you sure?',
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoadingData(true);
      const [studentsData, classroomsData] = await Promise.all([
        studentsApi.getAll({ limit: 1000 }),
        classroomsApi.getAll({ limit: 100 }),
      ]);
      setStudents(studentsData.data || []);
      setClassrooms(classroomsData.data || []);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des données');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSend = async () => {
    if (!message.trim()) {
      setError('Veuillez saisir un message');
      return;
    }

    if (tabValue === 0 && !selectedStudent) {
      setError('Veuillez sélectionner un élève');
      return;
    }

    if (tabValue === 1 && !selectedClassroom) {
      setError('Veuillez sélectionner une classe');
      return;
    }

    if (tabValue === 2) {
      const confirmed = window.confirm(currentText.confirmSchool);
      if (!confirmed) return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      setResult(null);

      let response: CommunicationResponse;

      if (tabValue === 0) {
        response = await communicationApi.sendToStudent(selectedStudent, message);
      } else if (tabValue === 1) {
        response = await communicationApi.sendToClassroom(selectedClassroom, message);
      } else {
        response = await communicationApi.sendToSchool(message);
      }

      setResult(response);
      setSuccess(currentText.messageSent);
      setMessage('');
      setSelectedStudent('');
      setSelectedClassroom('');
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'envoi du message');
    } finally {
      setLoading(false);
    }
  };

  const getSmsCount = (text: string) => {
    const length = text.length;
    if (length === 0) return 0;
    if (length <= 160) return 1;
    return Math.ceil(length / 153);
  };

  if (loadingData) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: '#0D3C4C', mb: 1 }}>
          {currentText.title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {currentText.subtitle}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: result ? 3 : 0 }}>
        <CardContent>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
                  <Tab 
                    icon={<Users size={20} />} 
                    label={currentText.toStudent} 
                    iconPosition="start"
                  />
                  <Tab 
                    icon={<School size={20} />} 
                    label={currentText.toClassroom} 
                    iconPosition="start"
                  />
                  <Tab 
                    icon={<MessageSquare size={20} />} 
                    label={currentText.toSchool} 
                    iconPosition="start"
                  />
                </Tabs>
              </Box>

              <TabPanel value={tabValue} index={0}>
                <Autocomplete
                  options={students}
                  getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.matricule})`}
                  value={students.find(s => s._id === selectedStudent) || null}
                  onChange={(_, newValue) => setSelectedStudent(newValue?._id || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={currentText.selectStudent}
                      placeholder="Rechercher un élève..."
                    />
                  )}
                  sx={{ mb: 3 }}
                  noOptionsText="Aucun élève trouvé"
                />
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <Autocomplete
                  options={classrooms}
                  getOptionLabel={(option) => `${option.name} (${option.level})`}
                  value={classrooms.find(c => c._id === selectedClassroom) || null}
                  onChange={(_, newValue) => setSelectedClassroom(newValue?._id || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={currentText.selectClassroom}
                      placeholder="Rechercher une classe..."
                    />
                  )}
                  sx={{ mb: 3 }}
                  noOptionsText="Aucune classe trouvée"
                />
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <Alert severity="warning" sx={{ mb: 3 }}>
                  {currentText.confirmSchool}
                </Alert>
              </TabPanel>

              <TextField
                label={currentText.message}
                placeholder={currentText.messagePlaceholder}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                multiline
                rows={6}
                fullWidth
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  {currentText.messageLength}: {message.length} {currentText.characters} ({getSmsCount(message)} {currentText.smsCount})
                </Typography>
              </Box>

              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send size={20} />}
                onClick={handleSend}
                disabled={loading}
                fullWidth
                sx={{
                  backgroundColor: '#0D3C4C',
                  '&:hover': { backgroundColor: '#1A5F7A' },
                  py: 1.5,
                }}
              >
                {loading ? currentText.sending : currentText.send}
              </Button>
        </CardContent>
      </Card>

      {result && (
        <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D3C4C', mb: 3 }}>
                  {currentText.results}
                </Typography>

                {result.recipient && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {currentText.recipient}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">{currentText.studentName}:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {result.recipient.studentName}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">{currentText.parentName}:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {result.recipient.parentName}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">{currentText.parentPhone}:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {result.recipient.parentPhone}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {result.summary && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {currentText.summary}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0D3C4C' }}>
                            {result.summary.totalStudents}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {currentText.totalStudents}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0D3C4C' }}>
                            {result.summary.studentsWithPhone}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {currentText.studentsWithPhone}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                            {result.summary.messagesSent}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {currentText.messagesSent}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ffebee' }}>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: '#c62828' }}>
                            {result.summary.messagesFailed}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {currentText.messagesFailed}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {result.results && result.results.length > 0 && (
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>{currentText.studentName}</TableCell>
                          <TableCell>{currentText.status}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {result.results.slice(0, 10).map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {item.studentName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.matricule}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={item.status === 'envoyé' ? <CheckCircle size={16} /> : <XCircle size={16} />}
                                label={item.status}
                                size="small"
                                color={item.status === 'envoyé' ? 'success' : 'error'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default Communication;
